import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { Controller, FormProvider, useFormContext } from "react-hook-form";

import { cn } from "/lib/utils";
import { Label } from "/components/ui/label";

import Lottie from "lottie-react";
import successAnimation from "assets/animations/success.json";
import { tinaClient, useTina, tinaField } from "@/utils";
import { Button } from "./button";

const Form = FormProvider;

const FormFieldContext = React.createContext({});

const FormField = ({ ...props }) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

const FormItemContext = React.createContext({});

const FormItem = React.forwardRef(({ className, ...props }, ref) => {
  const id = React.useId();

  const { error, formItemId } = useFormField();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div
        ref={ref}
        className={cn(
          "!grid grid-cols-[1fr_120px] [&>label]:order-0 [&>p]:order-1 [&>input]:order-2 [&>textarea]:order-2 [&>input]:col-span-2 [&>textarea]:col-span-2 [&>button]:order-2 [&>button]:col-span-2 gap-2",
          error && "[&>input]:!border-red-500 [&>textarea]:!border-red-500 [&>button]:!border-red-500",
          className
        )}
        {...props}
      />
    </FormItemContext.Provider>
  );
});
FormItem.displayName = "FormItem";

const FormLabel = React.forwardRef(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField();

  return (
    <Label
      ref={ref}
      // className={cn("font-title", error && "text-red-500 dark:text-red-900", className)}
      className={cn("font-title text-base", className)}
      htmlFor={formItemId}
      {...props}
    />
  );
});
FormLabel.displayName = "FormLabel";

const FormControl = React.forwardRef(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
      aria-invalid={!!error}
      {...props}
    />
  );
});
FormControl.displayName = "FormControl";

const FormDescription = React.forwardRef(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField();

  return <p ref={ref} id={formDescriptionId} className={cn("text-sm text-dark/60", className)} {...props} />;
});
FormDescription.displayName = "FormDescription";

const FormMessage = React.forwardRef(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message) : children;

  if (!body) {
    return null;
  }

  return (
    <p ref={ref} id={formMessageId} className={cn("text-sm font-medium text-red-500 text-right", className)} {...props}>
      {body}
    </p>
  );
});
FormMessage.displayName = "FormMessage";

const FormSuccess = React.forwardRef(({ className, formSuccessDetails, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "absolute w-full bg-white bg-opacity-[0.97] top-0 bottom-0 left-0 flex flex-col justify-center items-center gap-4 text-center px-4 md:px-8 -mt-12",
        "w-[calc(100%+16px] -mx-4",
        className
      )}
      {...props}
    >
      <Lottie animationData={successAnimation} loop={false} className="w-48 h-48" />
      <div className="h2 mt-4" data-tina-field={tinaField(formSuccessDetails, "successTitle")}>
        {formSuccessDetails?.successTitle}
      </div>
      <p data-tina-field={tinaField(formSuccessDetails, "successMessage")}>{formSuccessDetails?.successMessage}</p>
      {children}
    </div>
  );
});

const FormGeneralError = ({ form }) => {
  if (!form?.formState?.errors?.general) {
    return null;
  }

  return <div className="text-red-500">{form?.formState?.errors?.general?.message}</div>;
};

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
  FormSuccess,
  FormGeneralError,
};
